import React from "react";
import { Link } from "react-router-dom";
import { Users, Sparkles, ShieldCheck, Mail } from "lucide-react";

function Page({ title, children }) {
  return (
    <div className="max-w-3xl mx-auto px-5 py-12">
      <h1 className="text-4xl font-black tracking-tighter" style={{ color: "#1E293B" }}>{title}</h1>
      <div className="mt-6 prose-notebook">{children}</div>
    </div>
  );
}

export function About() {
  return (
    <Page title="About Acuman">
      <p>Acuman is a personal AI knowledge synthesizer. Instead of trusting a single AI model, you pose one question to a
        <strong> council</strong> of several models at once, then a powerful Synthesizer agent distills their answers into one clear, balanced response.</p>
      <div className="grid sm:grid-cols-3 gap-4 my-6 not-prose">
        {[[Users, "The Council", "GPT-5.4 Mini, Claude Haiku 4.5 & Gemini 3 Flash answer in parallel."],
          [Sparkles, "The Synthesizer", "Claude Sonnet 4.6 reconciles agreement and disagreement into one answer."],
          [ShieldCheck, "Local-First", "Every session lives in your browser via IndexedDB. Nothing is stored on a server."]].map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
            <Icon size={20} color="#2563EB" />
            <h3 className="text-base font-bold mt-3" style={{ color: "#1E293B" }}>{t}</h3>
            <p className="text-sm mt-1" style={{ color: "#475569" }}>{d}</p>
          </div>
        ))}
      </div>
      <p>Acuman is built by an independent maker who believes better decisions come from synthesizing diverse perspectives —
        not from echo chambers. There are no ads and no subscriptions.</p>
    </Page>
  );
}

export function Mission() {
  return (
    <Page title="Our Mission">
      <p>We believe the best decisions come from weighing multiple perspectives. A single AI model can be confidently wrong;
        a council of models, carefully synthesized, gives you a richer, more honest picture.</p>
      <p>Acuman exists to <strong>empower you to make better decisions</strong> by turning scattered questions into connected,
        structured insight over time. It is a thinking tool, not an oracle — designed for reflection, clarity, and growth.</p>
      <p>We are committed to privacy-first software: your thoughts are yours. Everything stays on your device.</p>
    </Page>
  );
}

export function Help() {
  const faqs = [
    ["How does the AI council work?", "Your question is sent to three different AI models simultaneously. Each answers independently, then a Synthesizer model combines them into one balanced response and tags it for your library."],
    ["Where is my data stored?", "Entirely in your browser using IndexedDB. Your questions and answers never leave your device except momentarily to generate AI responses."],
    ["What do I get for free?", "The first 5 Q&A sessions are completely free. After that, a one-time $5 payment unlocks unlimited sessions forever — no subscriptions."],
    ["What is an Insight Worksheet?", "Select 2–5 related sessions in your Library and Acuman generates a structured document: a theme, connection map, key insights, action steps, a new idea, and related questions."],
    ["Can I use my voice?", "Yes. Use the microphone on the Ask page to dictate questions, and the Listen button in Conversation to hear answers read aloud."],
    ["Can I back up my data?", "Yes — Settings → Export All Data downloads your entire library as a single .json file."],
  ];
  return (
    <Page title="Help & FAQ">
      <div className="space-y-4 not-prose">
        {faqs.map(([q, a]) => (
          <div key={q} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
            <h3 className="font-bold" style={{ color: "#1E293B" }}>{q}</h3>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "#475569" }}>{a}</p>
          </div>
        ))}
      </div>
    </Page>
  );
}

export function Contact() {
  const [sent, setSent] = React.useState(false);
  return (
    <Page title="Contact">
      <p>Questions, feedback, or ideas? We'd love to hear from you. Email
        <strong> support@acuman.online</strong> or use the form below.</p>
      {sent ? (
        <div className="rounded-2xl border p-6 not-prose mt-4" style={{ borderColor: "#10B98155", background: "#ECFDF5" }} data-testid="contact-success">
          <p className="font-semibold" style={{ color: "#047857" }}>Thanks! Your message has been noted.</p>
        </div>
      ) : (
        <form className="not-prose mt-4 space-y-4" onSubmit={(e) => { e.preventDefault(); setSent(true); }}>
          {["Your name", "Your email"].map((p, i) => (
            <input key={p} required type={i ? "email" : "text"} placeholder={p} data-testid={`contact-${i ? "email" : "name"}`}
              className="w-full h-11 px-4 rounded-xl border outline-none text-sm focus:ring-2" style={{ borderColor: "#CBD5E1", color: "#1E293B" }} />
          ))}
          <textarea required placeholder="How can we help?" data-testid="contact-message"
            className="w-full min-h-[120px] p-4 rounded-xl border outline-none text-sm" style={{ borderColor: "#CBD5E1", color: "#1E293B" }} />
          <button type="submit" data-testid="contact-submit"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-bold text-white" style={{ background: "#2563EB" }}>
            <Mail size={16} /> Send message
          </button>
        </form>
      )}
    </Page>
  );
}

export function Disclaimer() {
  return (
    <Page title="Disclaimer">
      <div className="rounded-2xl border p-5 not-prose mb-5" style={{ borderColor: "#F59E0B55", background: "#FFFBEB" }}>
        <p className="font-semibold" style={{ color: "#92400E" }}>
          AI-generated content is for informational and self-reflection purposes only.
        </p>
      </div>
      <p>Acuman produces AI-generated responses that may be inaccurate, incomplete, or out of date. Nothing in this app
        constitutes professional advice.</p>
      <p><strong>It is not a substitute for professional medical, legal, financial, or therapeutic advice.</strong> Always
        consult a qualified professional before making important decisions. Never disregard professional advice or delay
        seeking it because of something you read here.</p>
      <p>For details on how we handle your data, see our <Link to="/privacy" style={{ color: "#2563EB", fontWeight: 600 }}>Privacy Policy</Link>.</p>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy">
      <p><strong>Short version: we don't collect your data.</strong> Acuman is local-first.</p>
      <h3>What we collect</h3>
      <ul>
        <li>Nothing is stored on our servers. Your questions, AI answers, tags, and worksheets are saved only in your browser via IndexedDB.</li>
        <li>To generate answers, your question is sent transiently to AI providers to produce a response, then returned to you. It is not retained by Acuman.</li>
      </ul>
      <h3>How it's used</h3>
      <ul>
        <li>Your local data is used solely to power the app on your device.</li>
        <li>We never sell, rent, or share your data with third parties.</li>
      </ul>
      <h3>Payments</h3>
      <ul><li>One-time unlock payments are processed securely by Stripe. We never see or store your card details.</li></ul>
      <h3>Your control</h3>
      <ul><li>Export your full library anytime from Settings. Clearing your browser data removes everything permanently.</li></ul>
    </Page>
  );
}

export function Terms() {
  return (
    <Page title="Terms of Service">
      <p>By using Acuman you agree to these terms.</p>
      <h3>Acceptable use</h3>
      <ul>
        <li>Use Acuman lawfully and for personal knowledge and reflection.</li>
        <li>Do not attempt to disrupt, reverse engineer, or abuse the service or its AI providers.</li>
      </ul>
      <h3>No warranty</h3>
      <ul><li>The app and its AI content are provided "as is" without warranties of any kind. See our <Link to="/disclaimer" style={{ color: "#2563EB", fontWeight: 600 }}>Disclaimer</Link>.</li></ul>
      <h3>Payments</h3>
      <ul><li>The one-time unlock is a single $5 purchase. There are no subscriptions or recurring charges.</li></ul>
      <h3>Limitation of liability</h3>
      <ul><li>Acuman is not liable for decisions made based on AI-generated content.</li></ul>
    </Page>
  );
}
